import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Box, Card, Chip, Divider, IconButton, Typography, useTheme } from '@mui/material';
import { DateRange, Delete, Gavel } from '@mui/icons-material';

import { useHotelMetadata } from '@frontend/features/hotel-content';
import { formatISOStringToFriendlyDate, toProperName } from '@frontend/features/common/helper';

import { HotelFareBreakdownModal, Surcharge } from '../HotelFareBreakdown';
import { HotelCancelPolicyIndicator, LinkStyleButton } from '../HotelCancelPolicy';
import { HotelCancelPenalty } from '../HotelCancelPolicy/modal';
import { HotelFareRemarks } from '../HotelFareRemarks';
import { LoadingButton } from '@mui/lab';
import { HotelFareFinalDto } from '@bandido/shared/dtos';

interface Props {
  fare: HotelFareFinalDto;
  onSelectFare?: (fare: HotelFareFinalDto) => void;
  onRemoveFromCart?: () => void;
  quotationStatus?: {
    inProgress: boolean;
    fareId: number;
  };
}

export const HotelFareCard = ({
  fare,
  onSelectFare,
  onRemoveFromCart,
  quotationStatus,
}: Props) => {
  const [breakdownModal, setBreakdownModal] = useState(false);
  const toggleBreakdownModal = () => setBreakdownModal(prev => !prev);
  const {
    finalPrice: price,
    currency,
    finalPrice,
    accessCode,
    markupValue,
    markupIsFixed,
    surcharges,
    net,
    gross } = fare;
  const { rooms } = fare;
  const { occupancies } = fare;
  const { t } = useTranslation();
  const theme = useTheme();
  const { findSupplierByAccessCode } = useHotelMetadata();

  return (
    <Box width="100%">
      <HotelFareBreakdownModal
        open={breakdownModal}
        onClose={toggleBreakdownModal}
        net={net}
        gross={gross as number}
        finalPrice={finalPrice}
        markupIsFixed={markupIsFixed}
        markupValue={markupValue}
        surcharges={surcharges ? fare.surcharges as Surcharge[] : []}
      />
      <Card sx={{ p: theme.spacing(3) }} elevation={4}>
        <Box
          display="flex"
          gap={3}
          width="100%"
          minHeight="200px"
          alignItems="stretch"
        >
          <Box
            flex={1}
            display="flex"
            flexDirection="column"
            gap={3}
            justifyContent="space-between"
          >

            <Box display="flex" gap={1}>
              <Typography variant="h6">
                {toProperName(fare.propertyName)}
              </Typography>
            </Box>

            {/* ROOMS */}
            <Box
              gap={1}
              display="flex"
              flexDirection="column"
              alignSelf="stretch"
            >
              {rooms?.map((room, idx) => (
                <Box key={idx} display="flex" alignItems="center" gap={1}>
                  <Typography variant="subtitle2">
                    1x {room.description}
                  </Typography>

                  <Typography variant="body2" color={theme.palette.text.disabled}>
                    {occupancies && occupancies[idx]?.paxes?.length || 0} pax
                  </Typography>
                </Box>
              ))}

            </Box>

            <Box display="flex" justifyContent="space-between" alignItems="center">
              <Chip size='medium' label={fare.boardDescription?.charAt(0).toUpperCase() + fare.boardDescription?.slice(1).toLowerCase()} />
            </Box>


            {/* DETAILS */}
            <Box
              display="flex"
              flexDirection="column"
              gap={1}
            >
              {/* CHECKING / CHECKOUT */}
              <Box
                display="flex"
                gap={2}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <DateRange htmlColor={theme.palette.text.disabled} />
                  <Typography variant="subtitle2">Check-in: </Typography>
                  <Typography variant="body2" color={theme.palette.text.disabled}>{formatISOStringToFriendlyDate(fare.checkinDate)}</Typography>
                </Box>
                <Divider orientation="vertical" />
                <Box display="flex" alignItems="center" gap={1}>
                  <DateRange htmlColor={theme.palette.text.disabled} />
                  <Typography variant="subtitle2">Check-out: </Typography>
                  <Typography variant="body2" color={theme.palette.text.disabled}>{formatISOStringToFriendlyDate(fare.checkoutDate)}</Typography>
                </Box>
              </Box>

              {/* PAYMENT DETAILS */}

              <Box display="flex" gap={1}>

                <Gavel htmlColor={theme.palette.text.disabled} />
                <Typography variant="subtitle2">{t(`hotels.fares.rateRules.title`)}</Typography>
                {fare.rateRules?.length ? fare.rateRules?.map((rateRule) => {
                  return (
                    <Chip
                      key={rateRule}
                      label={t(`hotels.fares.rateRules.values.${rateRule}`)}
                      size='small'
                    />
                  )
                }) : t('common.notValueFound')}
              </Box>
              <Box display="flex" gap={1} justifyContent="space-between" alignItems="center">
                {/* CANCELATION POLICY */}
                <HotelCancelPolicyIndicator
                  refundable={fare.cancelPolicy?.refundable}
                  cancelPolicy={{
                    refundable: fare.cancelPolicy?.refundable,
                    gross: fare.cancelPolicy?.gross,
                    cancelPenalties: fare.cancelPolicy?.cancelPenalties?.map((penalty: HotelCancelPenalty) => ({
                      deadline: penalty.deadline,
                      isCalculatedDeadline: penalty.isCalculatedDeadline,
                      penaltyType: penalty.penaltyType,
                      currency: penalty.currency,
                      value: penalty.value
                    })) || [],
                    description: fare.cancelPolicy?.description
                  }}
                />

                {fare.remarks && (
                  <HotelFareRemarks remarks={fare.remarks} />
                )}
              </Box>
            </Box>
          </Box>

          <Divider orientation="vertical" flexItem />

          <Box display="flex" flexDirection="column" justifyContent="space-between" alignItems="center" minWidth="200px" >
            <Box width="100%" display="flex" flexDirection="row-reverse">
              <Chip label={findSupplierByAccessCode(accessCode)?.supplierName} color="secondary" />
            </Box>

            <Box display="flex" gap={1} alignItems="center" flexDirection="column">
              <Typography variant="h6" fontWeight={700} color="primary">{currency} {price ? price.toFixed(2) : finalPrice?.toFixed(2)}</Typography>
              <LinkStyleButton onClick={toggleBreakdownModal}>Ver detalles</LinkStyleButton>
            </Box>

            <Box display="flex" gap={1} width="100%">
              { 
                onSelectFare && (
                  <LoadingButton
                    loading={quotationStatus?.inProgress && quotationStatus?.fareId === fare.id}
                    disabled={quotationStatus?.inProgress && quotationStatus?.fareId !== fare.id}
                    onClick={() => onSelectFare(fare)}
                    color="primary"
                    variant="contained"
                    fullWidth
                  >
                    {t('common.continue')}
                  </LoadingButton>
                )
              }
              {onRemoveFromCart &&
                <Box width="100%" display="flex" justifyContent="flex-end">
                  <IconButton color='error' onClick={onRemoveFromCart}>
                    <Delete />
                  </IconButton>
                </Box>
              }
            </Box>
          </Box>
        </Box>
      </Card>
    </Box>
  );
};
